import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, Plus, X, RefreshCw, Grid, Edit, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useRiskContext } from './context/RiskContext.jsx';

// Define the true defaults outside the component
const MATRIX_DEFAULTS = {
  likelihoodLevels: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
  probabilityLevels: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
  impactLevels: ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'],
  scoreThresholds: [
    { label: 'Low', value: 6, color: '#22c55e' },
    { label: 'Medium', value: 12, color: '#fde047' },
    { label: 'High', value: 19, color: '#f59e42' },
    { label: 'Critical', value: 25, color: '#dc2626' }
  ],
  calculationType: 'linear'
};

const RiskImpactMatrix = ({ viewMode = 'both', hideFilters = false, hideKanban = false, hideRegister = false }) => {
  const [localRisks, setLocalRisks] = useState([
    { 
      id: 1, 
      description: "Key team member leaves", 
      likelihood: 2, 
      impact: 4, 
      category: "Resource", 
      action: "Mitigate",
      actionDescription: "Cross-train team members and create documentation",
      actionOwner: "HR Manager",
      deadline: "2025-07-15",
      status: "In Progress"
    },
    { 
      id: 2, 
      description: "Budget overrun", 
      likelihood: 3, 
      impact: 3, 
      category: "Financial", 
      action: "Monitor",
      actionDescription: "Weekly budget tracking and variance reports",
      actionOwner: "Finance Lead",
      deadline: "2025-07-30",
      status: "Not Started"
    },
    { 
      id: 3, 
      description: "Client changes requirements", 
      likelihood: 4, 
      impact: 3, 
      category: "Scope", 
      action: "Accept",
      actionDescription: "Establish change control process",
      actionOwner: "Project Manager",
      deadline: "2025-07-10",
      status: "Completed"
    }
  ]);
  
  const [showMatrixConfig, setShowMatrixConfig] = useState(false);
  const [draggedRisk, setDraggedRisk] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showRiskDetail, setShowRiskDetail] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    action: '',
    owner: '',
    deadline: ''
  });

  const ctx = useRiskContext(); // may be undefined if no provider
  const [localMatrix, setLocalMatrix] = useState({ ...MATRIX_DEFAULTS });
  const matrixConfig = ctx && ctx.matrixConfig ? ctx.matrixConfig : localMatrix;
  const setMatrixConfig = ctx && ctx.setMatrixConfig ? ctx.setMatrixConfig : setLocalMatrix;
  



  const categories = ['Technical', 'Financial', 'Resource', 'Scope', 'External', 'Quality'];
  const riskActions = ['Monitor', 'Mitigate', 'Transfer', 'Accept', 'Avoid', 'Escalate'];
  const statusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Overdue'];
  
  const actionColors = {
    'Monitor': 'bg-blue-100 text-blue-800 border-blue-200',
    'Mitigate': 'bg-green-100 text-green-800 border-green-200',
    'Transfer': 'bg-purple-100 text-purple-800 border-purple-200',
    'Accept': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Avoid': 'bg-red-100 text-red-800 border-red-200',
    'Escalate': 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const categoryColors = {
    'Technical': 'bg-blue-500',
    'Financial': 'bg-green-500',
    'Resource': 'bg-purple-500',
    'Scope': 'bg-orange-500',
    'External': 'bg-red-500',
    'Quality': 'bg-yellow-500'
  };

  const [dashboardTooltip, setDashboardTooltip] = useState({ idx: null, x: 0, y: 0 });

  const [selectedMatrixDimension, setSelectedMatrixDimension] = useState(5); // default 5x5

  const [showScoreLabels, setShowScoreLabels] = useState(false);
  
  // ================= Dynamic color grouping (matrix dots & legend) =================
  const [colorBy, setColorBy] = useState('Category');
  
  const colorPalette = ['#3b82f6','#10b981','#8b5cf6','#f97316','#ef4444','#eab308','#0ea5e9','#14b8a6','#6366f1','#facc15'];
  
  const predefinedCategoryHex = {
    'Technical': '#3b82f6',
    'Financial': '#10b981',
    'Resource': '#8b5cf6',
    'Scope': '#f97316',
    'External': '#ef4444',
    'Quality': '#eab308'
  };
  const predefinedStatusHex = {
    'Not Started': '#64748b',
    'In Progress': '#3b82f6',
    'Completed': '#10b981',
    'On Hold': '#facc15',
    'Overdue': '#dc2626'
  };
  const predefinedActionHex = {
    'Monitor': '#3b82f6',
    'Mitigate': '#10b981',
    'Transfer': '#8b5cf6',
    'Accept': '#facc15',
    'Avoid': '#ef4444',
    'Escalate': '#f97316'
  };
  
  // color helpers will be defined after getUniqueOwners
  
  const [kanbanGrouping, setKanbanGrouping] = useState('Status');

  // Inline editing state for Risk Register table
  const [editingCell, setEditingCell] = useState(null); // { riskId, field }
  const [editingValue, setEditingValue] = useState('');

  // Tooltip for risks on main matrix
  const [matrixTooltip, setMatrixTooltip] = useState({ show: false, risk: null, x: 0, y: 0 });

  // Fullscreen toggle for Kanban
  const [kanbanFullscreen, setKanbanFullscreen] = useState(false);

  // Sorting state for risk register
  const [sortConfig, setSortConfig] = useState({ key: 'riskScore', direction: 'desc' });

  // Force re-render key for sample data loading
  const [forceRenderKey, setForceRenderKey] = useState(0);
  
  // Sample data set state
  const [sampleDataSet, setSampleDataSet] = useState('8'); // '3', '8', or '20'

  // Fullscreen toggle for Risk Register
  const [registerFullscreen, setRegisterFullscreen] = useState(false);

  const risks = ctx && ctx.risks ? ctx.risks : localRisks;
  const setRisks = ctx && ctx.setRisks ? ctx.setRisks : setLocalRisks;

  const updateRisk = (riskId, field, value) => {
    setRisks(risks.map(risk => 
      risk.id === riskId ? { ...risk, [field]: value } : risk
    ));
  };

  const removeRisk = (id) => {
    setRisks(risks.filter(risk => risk.id !== id));
  };

  const getSampleDataSet = (setSize) => {
    const smallSet = [
      { id: 1, description: "Budget overrun", probability: 3, impact: 4, category: "Financial", action: "Monitor", actionDescription: "Weekly budget tracking", actionOwner: "Finance Lead", deadline: "2025-07-30", status: "Not Started" },
      { id: 2, description: "Key team member leaves", probability: 2, impact: 4, category: "Resource", action: "Mitigate", actionDescription: "Cross-train team members", actionOwner: "HR Manager", deadline: "2025-07-15", status: "In Progress" },
      { id: 3, description: "Client changes requirements", probability: 4, impact: 3, category: "Scope", action: "Accept", actionDescription: "Change control process", actionOwner: "Project Manager", deadline: "2025-07-10", status: "Completed" }
    ];

    const mediumSet = [
      { id: 1, description: "Key team member leaves", probability: 2, impact: 4, category: "Resource", action: "Mitigate", actionDescription: "Cross-train team members and create documentation", actionOwner: "HR Manager", deadline: "2025-07-15", status: "In Progress" },
      { id: 2, description: "Budget overrun", probability: 4, impact: 4, category: "Financial", action: "Avoid", actionDescription: "Add contingency budget and frequent cost reviews", actionOwner: "Finance Lead", deadline: "2025-08-01", status: "On Hold" },
      { id: 3, description: "Client changes requirements mid-project", probability: 5, impact: 5, category: "Scope", action: "Escalate", actionDescription: "Formal change control board approvals required", actionOwner: "Project Sponsor", deadline: "2025-07-20", status: "Not Started" },
      { id: 4, description: "Technology stack becomes obsolete", probability: 3, impact: 3, category: "Technical", action: "Monitor", actionDescription: "Quarterly technology assessment", actionOwner: "Tech Lead", deadline: "2025-09-15", status: "In Progress" },
      { id: 5, description: "Vendor delays delivery", probability: 3, impact: 2, category: "External", action: "Transfer", actionDescription: "Include penalty clauses in contracts", actionOwner: "Procurement", deadline: "2025-07-05", status: "Overdue" },
      { id: 6, description: "Quality standards not met", probability: 2, impact: 2, category: "Quality", action: "Mitigate", actionDescription: "Implement peer review process", actionOwner: "QA Manager", deadline: "2025-07-20", status: "In Progress" },
      { id: 7, description: "Data breach or security incident", probability: 2, impact: 5, category: "External", action: "Avoid", actionDescription: "Implement multi-factor authentication and regular audits", actionOwner: "Security Lead", deadline: "2025-07-25", status: "Not Started" },
      { id: 8, description: "Critical hardware failure", probability: 1, impact: 4, category: "Technical", action: "Mitigate", actionDescription: "Set up redundant hardware and backups", actionOwner: "Infra Team", deadline: "2025-08-10", status: "Completed" }
    ];

    const largeSet = [
      { id: 1, description: "Key team member leaves", probability: 2, impact: 4, category: "Resource", action: "Mitigate", actionDescription: "Cross-train team members and create documentation", actionOwner: "HR Manager", deadline: "2025-07-15", status: "In Progress" },
      { id: 2, description: "Budget overrun", probability: 4, impact: 4, category: "Financial", action: "Avoid", actionDescription: "Add contingency budget and frequent cost reviews", actionOwner: "Finance Lead", deadline: "2025-08-01", status: "On Hold" },
      { id: 3, description: "Client changes requirements mid-project", probability: 5, impact: 5, category: "Scope", action: "Escalate", actionDescription: "Formal change control board approvals required", actionOwner: "Project Sponsor", deadline: "2025-07-20", status: "Not Started" },
      { id: 4, description: "Technology stack becomes obsolete", probability: 3, impact: 3, category: "Technical", action: "Monitor", actionDescription: "Quarterly technology assessment", actionOwner: "Tech Lead", deadline: "2025-09-15", status: "In Progress" },
      { id: 5, description: "Vendor delays delivery", probability: 3, impact: 2, category: "External", action: "Transfer", actionDescription: "Include penalty clauses in contracts", actionOwner: "Procurement", deadline: "2025-07-05", status: "Overdue" },
      { id: 6, description: "Quality standards not met", probability: 2, impact: 2, category: "Quality", action: "Mitigate", actionDescription: "Implement peer review process", actionOwner: "QA Manager", deadline: "2025-07-20", status: "In Progress" },
      { id: 7, description: "Data breach or security incident", probability: 2, impact: 5, category: "External", action: "Avoid", actionDescription: "Implement multi-factor authentication and regular audits", actionOwner: "Security Lead", deadline: "2025-07-25", status: "Not Started" },
      { id: 8, description: "Critical hardware failure", probability: 1, impact: 4, category: "Technical", action: "Mitigate", actionDescription: "Set up redundant hardware and backups", actionOwner: "Infra Team", deadline: "2025-08-10", status: "Completed" },
      { id: 9, description: "Database performance degradation", probability: 3, impact: 3, category: "Technical", action: "Monitor", actionDescription: "Database performance monitoring and optimization", actionOwner: "DBA", deadline: "2025-08-15", status: "Not Started" },
      { id: 10, description: "Regulatory compliance changes", probability: 4, impact: 4, category: "External", action: "Monitor", actionDescription: "Track regulatory updates and assess impact", actionOwner: "Compliance Officer", deadline: "2025-09-01", status: "In Progress" },
      { id: 11, description: "Third-party API changes", probability: 3, impact: 3, category: "Technical", action: "Monitor", actionDescription: "Monitor API deprecation notices", actionOwner: "Tech Lead", deadline: "2025-08-20", status: "Not Started" },
      { id: 12, description: "Market conditions affect project viability", probability: 2, impact: 5, category: "External", action: "Accept", actionDescription: "Regular market analysis and pivot planning", actionOwner: "Business Analyst", deadline: "2025-09-30", status: "Not Started" },
      { id: 13, description: "Scope creep without budget increase", probability: 4, impact: 3, category: "Scope", action: "Mitigate", actionDescription: "Strict change control process", actionOwner: "Project Manager", deadline: "2025-07-25", status: "In Progress" },
      { id: 14, description: "Communication breakdown between teams", probability: 3, impact: 3, category: "Resource", action: "Mitigate", actionDescription: "Regular cross-team meetings and clear communication protocols", actionOwner: "Scrum Master", deadline: "2025-07-12", status: "Completed" },
      { id: 15, description: "Testing phase delays", probability: 3, impact: 4, category: "Quality", action: "Mitigate", actionDescription: "Parallel testing and early test planning", actionOwner: "QA Manager", deadline: "2025-08-05", status: "In Progress" },
      { id: 16, description: "Integration issues with legacy systems", probability: 4, impact: 4, category: "Technical", action: "Mitigate", actionDescription: "Early integration testing and fallback plans", actionOwner: "Integration Lead", deadline: "2025-08-12", status: "Not Started" },
      { id: 17, description: "Lack of user adoption", probability: 2, impact: 4, category: "External", action: "Mitigate", actionDescription: "User training and change management program", actionOwner: "Change Manager", deadline: "2025-09-20", status: "On Hold" },
      { id: 18, description: "Performance bottlenecks in production", probability: 3, impact: 4, category: "Technical", action: "Mitigate", actionDescription: "Load testing and performance optimization", actionOwner: "Performance Engineer", deadline: "2025-08-25", status: "Not Started" },
      { id: 19, description: "Intellectual property disputes", probability: 1, impact: 5, category: "External", action: "Transfer", actionDescription: "Legal review and IP insurance", actionOwner: "Legal Team", deadline: "2025-07-18", status: "Overdue" },
      { id: 20, description: "Skills gap in emerging technologies", probability: 3, impact: 3, category: "Resource", action: "Mitigate", actionDescription: "Training programs and contractor support", actionOwner: "Training Manager", deadline: "2025-08-30", status: "In Progress" }
    ];

    switch (setSize) {
      case '3': return smallSet;
      case '8': return mediumSet;
      case '20': return largeSet;
      default: return mediumSet;
    }
  };

  const loadSampleData = useCallback(() => {
    // Cycle to next sample size
    const sizes = ['3', '8', '20'];
    const currentIndex = sizes.indexOf(sampleDataSet);
    const nextSize = sizes[(currentIndex + 1) % sizes.length];
    setSampleDataSet(nextSize);
    
    console.log(`🔄 Loading ${nextSize} sample records...`);
    console.log('Current risks before clear:', risks.length);
    
    // Force component re-render by updating key
    setForceRenderKey(prev => prev + 1);
    
    // Clear all state first
    setRisks([]);
    
    // Multiple step approach for maximum reliability
    setTimeout(() => {
      console.log('💥 Step 1: Clearing complete');
      
      const sampleRisks = getSampleDataSet(nextSize);
      
      console.log('✅ Step 2: Sample data prepared:', sampleRisks.length, 'risks');
      
      // Set new data with another timeout to ensure state update
      setTimeout(() => {
        setRisks([...sampleRisks]);
        setForceRenderKey(prev => prev + 1); // Force another re-render
        console.log('🎉 Step 3: Sample data loaded successfully');
      }, 100);
      
    }, 300); // Longer initial timeout for production builds
  }, [sampleDataSet, risks]);

  const getRiskPriority = (likelihood, impact) => {
    const score = likelihood * impact;
    if (!matrixConfig.scoreThresholds || !Array.isArray(matrixConfig.scoreThresholds)) {
      return { level: 'Low', color: '#22c55e' };
    }
    // Sort thresholds ascending by value (same as matrix cell logic)
    const sorted = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
    for (const threshold of sorted) {
      if (score <= threshold.value) {
        return { level: threshold.label, color: threshold.color || '#22c55e' };
      }
    }
    // If score exceeds highest threshold, use highest level
    const highest = sorted[sorted.length - 1];
    return { level: highest.label, color: highest.color || '#22c55e' };
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      category: '',
      action: '',
      owner: '',
      deadline: ''
    });
  };

  const getFilteredRisks = () => {
    return risks.filter(risk => {
      if (filters.status && risk.status !== filters.status) return false;
      if (filters.category && risk.category !== filters.category) return false;
      if (filters.action && risk.action !== filters.action) return false;
      if (filters.owner && !risk.actionOwner.toLowerCase().includes(filters.owner.toLowerCase())) return false;
      
      if (filters.deadline) {
        if (filters.deadline === 'overdue') {
          if (!risk.deadline || new Date(risk.deadline) >= new Date()) return false;
        } else if (filters.deadline === 'thisWeek') {
          if (!risk.deadline) return false;
          const deadlineDate = new Date(risk.deadline);
          const today = new Date();
          const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          if (deadlineDate < today || deadlineDate > weekFromNow) return false;
        } else if (filters.deadline === 'noDeadline') {
          if (risk.deadline) return false;
        }
      }
      
      return true;
    });
  };

  const getUniqueOwners = () => {
    return [...new Set(risks.map(risk => risk.actionOwner).filter(owner => owner))];
  };

  const getKanbanColumns = () => {
    switch (kanbanGrouping) {
      case 'Status':
        return statusOptions;
      case 'Category':
        return categories;
      case 'Action':
        return riskActions;
      case 'Owner': {
        const owners = getUniqueOwners();
        return owners.length > 0 ? owners : ['Unassigned'];
      }
      default:
        return [];
    }
  };

  const kanbanFieldMap = {
    'Status': 'status',
    'Category': 'category',
    'Action': 'action',
    'Owner': 'actionOwner'
  };

  const filteredRisks = getFilteredRisks();

  // --- Color helpers (now that all dependencies are defined) ---
  const colorMap = useMemo(() => {
    let baseMap = {};
    let values = [];
    switch (colorBy) {
      case 'Category':
        baseMap = { ...predefinedCategoryHex };
        values = categories;
        break;
      case 'Status':
        baseMap = { ...predefinedStatusHex };
        values = statusOptions;
        break;
      case 'Action':
        baseMap = { ...predefinedActionHex };
        values = riskActions;
        break;
      case 'Owner':
        values = [...new Set(risks.map(r => r.actionOwner || 'Unassigned'))];
        break;
      default:
        values = [];
    }
    values.forEach((val, idx) => {
      if (!baseMap[val]) baseMap[val] = colorPalette[idx % colorPalette.length];
    });
    return baseMap;
  }, [colorBy, categories, statusOptions, riskActions, risks]);

  const legendItems = useMemo(() => {
    switch (colorBy) {
      case 'Category':
        return categories;
      case 'Status':
        return statusOptions;
      case 'Action':
        return riskActions;
      case 'Owner':
        return [...new Set(risks.map(r => r.actionOwner || 'Unassigned'))];
      default:
        return [];
    }
  }, [colorBy, categories, statusOptions, riskActions, risks]);

  const getColorForRisk = (risk) => {
    const key = colorBy === 'Owner' ? (risk.actionOwner || 'Unassigned') :
                colorBy === 'Status' ? risk.status :
                colorBy === 'Action' ? risk.action :
                risk.category;
    return colorMap[key] || '#3b82f6';
  };

  // Drag and Drop Functions
  const handleDragStart = (e, risk) => {
    setDraggedRisk(risk);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedRisk(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, columnValue) => {
    e.preventDefault();
    setDragOverColumn(columnValue);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the column container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, newColumnValue) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedRisk) return;

    const field = kanbanFieldMap[kanbanGrouping];
    const currentValue = field === 'actionOwner' ? (draggedRisk[field] || 'Unassigned') : draggedRisk[field];
    if (currentValue !== newColumnValue) {
      // If owner column value is "Unassigned", store empty string
      const valueToStore = (field === 'actionOwner' && newColumnValue === 'Unassigned') ? '' : newColumnValue;
      updateRisk(draggedRisk.id, field, valueToStore);
    }
    setDraggedRisk(null);
  };

  // Risk Detail Functions
  const openRiskDetail = (risk) => {
    if (ctx && ctx.openRiskDetail) {
      ctx.openRiskDetail(risk);
    } else {
      setSelectedRisk(risk);
      setEditingRisk(risk);
      setShowRiskDetail(true);
    }
  };

  const closeRiskDetail = () => {
    if (ctx && ctx.closeRiskDetail) {
      ctx.closeRiskDetail();
    }
    setShowRiskDetail(false);
    setEditingRisk(null);
  };

  const saveRiskChanges = () => {
    if (editingRisk) {
      if (!selectedRisk) {
        // Adding a new risk
        setRisks([...risks, editingRisk]);
      } else {
        // Editing existing risk
        setRisks(risks.map(risk =>
          risk.id === editingRisk.id ? editingRisk : risk
        ));
      }
      closeRiskDetail();
    }
  };

  const handleEditRiskChange = (field, value) => {
    setEditingRisk(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add export functions at the top level of the component
  const exportToSpreadsheet = () => {
    const data = filteredRisks.map(risk => {
      const priority = getRiskPriority(risk.probability, risk.impact);
      return {
        ID: risk.id,
        Description: risk.description,
        Likelihood: risk.probability,
        Impact: risk.impact,
        Category: risk.category,
        Action: risk.action,
        'Action Description': risk.actionDescription,
        'Action Owner': risk.actionOwner,
        Deadline: risk.deadline,
        Status: risk.status,
        'Risk Score': priority.level
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Risk Register');
    XLSX.writeFile(wb, 'risk_register.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // --- Risk Impact Matrix First ---
    doc.text('Risk Impact Matrix', 14, 16);
    
    // Matrix configuration
    const matrixStartX = 14;
    const matrixStartY = 25;
    const cellSize = 20;
    const headerSize = 15;
    const impactLabels = matrixConfig.impactLevels;
    const likelihoodLabels = matrixConfig.probabilityLevels.slice().reverse(); // reverse for display
    
    // Helper function to convert hex color to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 34, g: 197, b: 94 }; // default green
    };
    
    // Draw matrix headers
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    
    // Impact header (top)
    doc.text('Impact', matrixStartX + headerSize + (impactLabels.length * cellSize) / 2 - 10, matrixStartY - 5);
    
    // Likelihood header (left, rotated)
    doc.text('Likelihood', matrixStartX - 5, matrixStartY + headerSize + (likelihoodLabels.length * cellSize) / 2);
    
    // Draw column headers (Impact)
    impactLabels.forEach((label, idx) => {
      const x = matrixStartX + headerSize + (idx * cellSize);
      const y = matrixStartY + headerSize - 2;
      doc.setFillColor(240, 240, 240);
      doc.rect(x, matrixStartY, cellSize, headerSize, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6);
      doc.text(label.substring(0, 8), x + cellSize/2, y, { align: 'center' });
    });
    
    // Draw row headers (Likelihood) and matrix cells
    likelihoodLabels.forEach((label, rowIdx) => {
      const y = matrixStartY + headerSize + (rowIdx * cellSize);
      
      // Row header
      doc.setFillColor(240, 240, 240);
      doc.rect(matrixStartX, y, headerSize, cellSize, 'FD');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6);
      doc.text(label.substring(0, 8), matrixStartX + headerSize/2, y + cellSize/2 + 2, { align: 'center' });
      
      // Matrix cells
      impactLabels.forEach((_, colIdx) => {
        const cellX = matrixStartX + headerSize + (colIdx * cellSize);
        const cellY = y;
        
        // Calculate score and color for this cell
        const prob = matrixConfig.probabilityLevels.length - rowIdx;
        const impact = colIdx + 1;
        let score;
        if (matrixConfig.calculationType === 'logarithmic') {
          score = Math.log10(prob * impact);
        } else {
          score = prob * impact;
        }
        
        // Find matching threshold color
        const sortedThresholds = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
        let cellColor = '#22c55e';
        for (const threshold of sortedThresholds) {
          if (score <= threshold.value) {
            cellColor = threshold.color || '#22c55e';
            break;
          }
        }
        
        // Draw colored cell
        const rgb = hexToRgb(cellColor);
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.rect(cellX, cellY, cellSize, cellSize, 'FD');
        
        // Draw cell border
        doc.setDrawColor(0, 0, 0);
        doc.rect(cellX, cellY, cellSize, cellSize, 'S');
      });
    });
    
    // Draw risk circles on matrix
    filteredRisks.forEach(risk => {
      const likelihoodValue = (risk.likelihood ?? risk.probability ?? 1); // fallback to probability for legacy data
      const rowIdx = matrixConfig.probabilityLevels.length - likelihoodValue; // reverse order
      const colIdx = risk.impact - 1;
      const centerX = matrixStartX + headerSize + (colIdx * cellSize) + cellSize/2;
      const centerY = matrixStartY + headerSize + (rowIdx * cellSize) + cellSize/2;
      
      // Draw circle with category color
      const categoryColorMap = {
        'Technical': '#3b82f6',
        'Financial': '#10b981', 
        'Resource': '#8b5cf6',
        'Scope': '#f97316',
        'External': '#ef4444',
        'Quality': '#eab308'
      };
      const circleColor = hexToRgb(categoryColorMap[risk.category] || '#3b82f6');
      
      // Draw white border circle
      doc.setFillColor(255, 255, 255);
      doc.circle(centerX, centerY, 4, 'F');
      
      // Draw colored circle
      doc.setFillColor(circleColor.r, circleColor.g, circleColor.b);
      doc.circle(centerX, centerY, 3, 'F');
      
      // Draw risk ID
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(6);
      doc.setFont(undefined, 'bold');
      doc.text(risk.id.toString(), centerX, centerY + 1, { align: 'center' });
    });
    
    // Add legend for categories
    let legendY = matrixStartY + headerSize + (likelihoodLabels.length * cellSize) + 10;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Categories:', matrixStartX, legendY);
    
    const categoryColorMap = {
      'Technical': '#3b82f6',
      'Financial': '#10b981', 
      'Resource': '#8b5cf6',
      'Scope': '#f97316',
      'External': '#ef4444',
      'Quality': '#eab308'
    };
    
    Object.entries(categoryColorMap).forEach(([category, color], idx) => {
      const legendX = matrixStartX + (idx % 3) * 60;
      const legendYPos = legendY + 8 + Math.floor(idx / 3) * 10;
      
      const rgb = hexToRgb(color);
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.circle(legendX, legendYPos, 2, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6);
      doc.setFont(undefined, 'normal');
      doc.text(category, legendX + 5, legendYPos + 1);
    });
    
    // --- Risk Register Table ---
    let tableStartY = legendY + 25;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Risk Register', 14, tableStartY);
    
    const tableColumn = [
      'ID', 'Description', 'Likelihood', 'Impact', 'Category', 'Action',
      'Action Description', 'Action Owner', 'Deadline', 'Status', 'Risk Score'
    ];
    const tableRows = filteredRisks.map(risk => {
      const priority = getRiskPriority(risk.probability, risk.impact);
      return [
        risk.id,
        risk.description,
        risk.probability,
        risk.impact,
        risk.category,
        risk.action,
        risk.actionDescription,
        risk.actionOwner,
        risk.deadline,
        risk.status,
        priority.level
      ];
    });
    
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: tableStartY + 8,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 4, right: 4 }
    });
    
    doc.save('risk_register.pdf');
  };

  // Add handler to open a blank risk detail modal
  const handleAddRiskModal = () => {
    if (!ctx) return;
    const newRisk = {
      id: `new-${Date.now()}`, // Temporary ID
      description: '',
      initialLikelihood: 1,
      initialImpact: 1,
      likelihood: 1,
      impact: 1,
      category: categories[0] || '',
      action: riskActions[0] || '',
      actionDescription: '',
      actionOwner: '',
      deadline: '',
      status: statusOptions[0] || ''
    };
    ctx.openRiskDetail(newRisk);
  };

  // Handler to reset to defaults
  const DEFAULT_DIM = 5;
  const DEFAULT_CALC_TYPE = 'linear';

  const resetMatrixConfig = () => {
    setSelectedMatrixDimension(DEFAULT_DIM);
    const config = getDefaultThresholds(DEFAULT_DIM, DEFAULT_CALC_TYPE);
    setMatrixConfig({
      calculationType: DEFAULT_CALC_TYPE,
      likelihoodLevels: [...config.likelihoods],
      probabilityLevels: [...config.likelihoods],
      impactLevels: [...config.impacts],
      scoreThresholds: config.thresholds.map(t => ({ ...t }))
    });
  };

  // Add a function to get default thresholds for a given dimension
  function getDefaultThresholds(dim, calcType) {
    // Table-based config for each dimension and calculation type
    const configs = {
      '3x3-linear': {
        thresholds: [
          { label: 'Low', value: 3, color: '#22c55e' },
          { label: 'Medium', value: 6, color: '#fde047' },
          { label: 'High', value: 9, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Likely'],
        impacts: ['Minor', 'Moderate', 'Major']
      },
      '3x3-logarithmic': {
        thresholds: [
          { label: 'Low', value: 0.48, color: '#22c55e' },
          { label: 'Medium', value: 0.78, color: '#fde047' },
          { label: 'High', value: 1.00, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Likely'],
        impacts: ['Minor', 'Moderate', 'Major']
      },
      '4x4-linear': {
        thresholds: [
          { label: 'Low', value: 4, color: '#22c55e' },
          { label: 'Medium', value: 8, color: '#fde047' },
          { label: 'High', value: 12, color: '#f59e42' },
          { label: 'Critical', value: 16, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely'],
        impacts: ['Insignificant', 'Minor', 'Moderate', 'Major']
      },
      '4x4-logarithmic': {
        thresholds: [
          { label: 'Low', value: 0.40, color: '#22c55e' },
          { label: 'Medium', value: 0.65, color: '#fde047' },
          { label: 'High', value: 0.85, color: '#f59e42' },
          { label: 'Critical', value: 1.21, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely'],
        impacts: ['Insignificant', 'Minor', 'Moderate', 'Major']
      },
      '5x5-linear': {
        thresholds: [
          { label: 'Low', value: 6, color: '#22c55e' },
          { label: 'Medium', value: 12, color: '#fde047' },
          { label: 'High', value: 19, color: '#f59e42' },
          { label: 'Critical', value: 25, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
        impacts: ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic']
      },
      '5x5-logarithmic': {
        thresholds: [
          { label: 'Low', value: 0.50, color: '#22c55e' },
          { label: 'Medium', value: 0.85, color: '#fde047' },
          { label: 'High', value: 1.15, color: '#f59e42' },
          { label: 'Critical', value: 1.40, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'],
        impacts: ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic']
      },
      '6x6-linear': {
        thresholds: [
          { label: 'Very Low', value: 7, color: '#15803d' },
          { label: 'Low', value: 14, color: '#22c55e' },
          { label: 'Medium', value: 21, color: '#fde047' },
          { label: 'High', value: 28, color: '#f59e42' },
          { label: 'Critical', value: 36, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain', 'Expected'],
        impacts: ['Negligible', 'Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic']
      },
      '6x6-logarithmic': {
        thresholds: [
          { label: 'Very Low', value: 0.35, color: '#15803d' },
          { label: 'Low', value: 0.65, color: '#22c55e' },
          { label: 'Medium', value: 0.95, color: '#fde047' },
          { label: 'High', value: 1.25, color: '#f59e42' },
          { label: 'Critical', value: 1.56, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain', 'Expected'],
        impacts: ['Negligible', 'Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic']
      },
      '7x7-linear': {
        thresholds: [
          { label: 'Very Low', value: 10, color: '#15803d' },
          { label: 'Low', value: 20, color: '#22c55e' },
          { label: 'Medium', value: 30, color: '#fde047' },
          { label: 'High', value: 40, color: '#f59e42' },
          { label: 'Critical', value: 49, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Very Unlikely', 'Unlikely', 'Possible', 'Likely', 'Almost Certain', 'Expected'],
        impacts: ['Negligible', 'Insignificant', 'Minor', 'Moderate', 'Major', 'Severe', 'Catastrophic']
      },
      '7x7-logarithmic': {
        thresholds: [
          { label: 'Very Low', value: 0.40, color: '#15803d' },
          { label: 'Low', value: 0.70, color: '#22c55e' },
          { label: 'Medium', value: 1.00, color: '#fde047' },
          { label: 'High', value: 1.35, color: '#f59e42' },
          { label: 'Critical', value: 1.70, color: '#dc2626' }
        ],
        likelihoods: ['Rare', 'Very Unlikely', 'Unlikely', 'Possible', 'Likely', 'Almost Certain', 'Expected'],
        impacts: ['Negligible', 'Insignificant', 'Minor', 'Moderate', 'Major', 'Severe', 'Catastrophic']
      }
    };
    const key = `${dim}x${dim}-${calcType.toLowerCase()}`;
    return configs[key] || configs['5x5-linear'];
  }

  useEffect(() => {
    console.log('matrixConfig updated:', matrixConfig);
  }, [matrixConfig]);

  // Add functions for inline editing near other helper functions
  const startEditing = (riskId, field, currentValue) => {
    setEditingCell({ riskId, field });
    setEditingValue(currentValue || '');
  };

  const saveInlineEdit = () => {
    if (editingCell) {
      updateRisk(editingCell.riskId, editingCell.field, editingValue);
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleInlineKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveInlineEdit();
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  // Add sorting functions near other helper functions
  const getSortedRisks = useCallback(() => {
    const sorted = [...filteredRisks].sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'riskScore') {
        const aPriority = getRiskPriority(a.probability, a.impact);
        const bPriority = getRiskPriority(b.probability, b.impact);
        // Create a score hierarchy: Critical=4, High=3, Medium=2, Low=1
        const scoreMap = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1, 'Very Low': 0 };
        aValue = scoreMap[aPriority.level] || 0;
        bValue = scoreMap[bPriority.level] || 0;
      } else if (sortConfig.key === 'deadline') {
        aValue = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
        bValue = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
      } else if (sortConfig.key === 'probability' || sortConfig.key === 'impact') {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      } else {
        aValue = a[sortConfig.key]?.toString().toLowerCase() || '';
        bValue = b[sortConfig.key]?.toString().toLowerCase() || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Secondary sort by deadline (oldest first) if primary sort is equal
      if (sortConfig.key !== 'deadline') {
        const aDeadline = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
        const bDeadline = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
        return aDeadline - bDeadline; // Ascending order (oldest first)
      }
      
      return 0;
    });
    
    return sorted;
  }, [filteredRisks, sortConfig, matrixConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortConfig.direction === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  // Reusable Risk Register table (interactive)
  const RiskRegisterTable = () => {
    const priorityColor = (risk) => getRiskPriority(risk.probability, risk.impact).color;

    const renderEditableCell = (risk, field, value, type = 'text') => {
      const isEditing = editingCell?.riskId === risk.id && editingCell?.field === field;

      if (isEditing) {
        if (type === 'select') {
          let options = [];
          if (field === 'category') options = categories;
          else if (field === 'action') options = riskActions;
          else if (field === 'status') options = statusOptions;
          else if (field === 'probability') options = Array.from({ length: matrixConfig.probabilityLevels.length }, (_, i) => i + 1);
          else if (field === 'impact') options = Array.from({ length: matrixConfig.impactLevels.length }, (_, i) => i + 1);

  return (
            <select
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={saveInlineEdit}
              onKeyDown={handleInlineKeyPress}
              className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          );
        } else if (type === 'textarea') {
          return (
            <textarea
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={saveInlineEdit}
              onKeyDown={handleInlineKeyPress}
              className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
              autoFocus
            />
          );
        } else {
          return (
            <input
              type={type}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={saveInlineEdit}
              onKeyDown={handleInlineKeyPress}
              className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          );
        }
      }

      return (
        <div
          className="cursor-pointer hover:bg-gray-50 p-1 rounded min-h-[24px]"
          onClick={() => startEditing(risk.id, field, value)}
          title="Click to edit"
        >
          {value || <span className="text-gray-400 italic">Click to edit</span>}
        </div>
      );
    };

    return (
      <table className="min-w-full border text-xs sm:text-sm">
        <thead>
          <tr className="bg-gray-100">
            {[
              { key: 'id', label: 'ID' },
              { key: 'description', label: 'Description' },
              { key: 'probability', label: 'Likelihood' },
              { key: 'impact', label: 'Impact' },
              { key: 'category', label: 'Category' },
              { key: 'action', label: 'Action' },
              { key: 'actionDescription', label: 'Action Description' },
              { key: 'actionOwner', label: 'Action Owner' },
              { key: 'deadline', label: 'Deadline' },
              { key: 'status', label: 'Status' },
              { key: 'riskScore', label: 'Risk Score' }
            ].map((col) => (
              <th
                key={col.key}
                className="border px-4 py-2 text-left cursor-pointer hover:bg-gray-200 select-none"
                onClick={() => handleSort(col.key)}
              >
                <div className="flex items-center justify-between">
                  {col.label} {getSortIcon(col.key)}
                </div>
              </th>
            ))}
            <th className="border px-4 py-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {getSortedRisks().length === 0 ? (
            <tr>
              <td colSpan={12} className="border px-4 py-2 text-center text-gray-400">
                No risks to display
              </td>
            </tr>
          ) : (
            getSortedRisks().map((risk) => {
              const priority = getRiskPriority(risk.probability, risk.impact);

              return (
                <tr key={risk.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2 font-mono text-sm">{risk.id}</td>
                  <td className="border px-2 py-2 max-w-xs">{renderEditableCell(risk, 'description', risk.description, 'textarea')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'probability', risk.probability, 'select')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'impact', risk.impact, 'select')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'category', risk.category, 'select')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'action', risk.action, 'select')}</td>
                  <td className="border px-2 py-2 max-w-xs">{renderEditableCell(risk, 'actionDescription', risk.actionDescription, 'textarea')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'actionOwner', risk.actionOwner)}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'deadline', risk.deadline, 'date')}</td>
                  <td className="border px-2 py-2">{renderEditableCell(risk, 'status', risk.status, 'select')}</td>
                  <td className="border px-4 py-2">
                    <span
                      className="px-2 py-1 rounded text-xs font-medium text-white"
                      style={{ backgroundColor: priorityColor(risk) }}
                    >
                      {priority.level}
                    </span>
                  </td>
                  <td className="border px-2 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => openRiskDetail(risk)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                        title="View/Edit Details"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this risk?')) {
                            removeRisk(risk.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                        title="Delete Risk"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div key={`risk-dashboard-${forceRenderKey}`} className="p-4 bg-white w-full">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
              <AlertTriangle className="text-orange-500" />
              Risk Assessment and Control Hub <span className="text-xl font-normal text-orange-600">(Beta)</span>
            </h1>
            <p className="text-gray-600">Transform your project risks into actionable insights</p>
          </div>
          
          <button
            onClick={() => setShowMatrixConfig(!showMatrixConfig)}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm"
          >
            Customize Matrix
          </button>
        </div>
        
        {showMatrixConfig && (
          <div className="relative mt-4 bg-blue-50 rounded-lg p-4">
            {/* Floating matrix preview (no longer pushes header) */}
            <div className="absolute top-4 right-4 bg-white rounded border p-2 shadow-sm">
                <div className="text-xs text-gray-600 mb-1 text-center">Preview</div>
                <div
                  className="grid gap-0 border border-gray-300"
                  style={{
                    gridTemplateColumns: `repeat(${matrixConfig.impactLevels.length + 1}, 1fr)`,
                    gridTemplateRows: `repeat(${matrixConfig.probabilityLevels.length + 1}, 1fr)`,
                  width: '100px',
                  height: '100px'
                  }}
                >
                  {/* Top-left empty cell */}
                  <div className="border border-gray-300 bg-gray-100" style={{ fontSize: '6px' }} />
                  {/* Column headers */}
                  {matrixConfig.impactLevels.map((label, idx) => (
                    <div
                      key={`preview-col-${idx}`}
                      className="border border-gray-300 bg-gray-100 flex items-center justify-center"
                      style={{ fontSize: '6px', padding: '1px' }}
                    >
                      {label.substring(0, 3)}
                    </div>
                  ))}
                  {/* Row headers and matrix cells */}
                  {matrixConfig.probabilityLevels.slice().reverse().map((rowLabel, rowIdx) => [
                    // Row header
                    <div
                      key={`preview-row-${rowIdx}`}
                      className="border border-gray-300 bg-gray-100 flex items-center justify-center"
                      style={{ fontSize: '6px', padding: '1px' }}
                    >
                      {rowLabel.substring(0, 3)}
                    </div>,
                    // Matrix cells for this row
                    ...matrixConfig.impactLevels.map((_, colIdx) => {
                      const prob = matrixConfig.probabilityLevels.length - rowIdx;
                      const impact = colIdx + 1;
                      let score;
                      if (matrixConfig.calculationType === 'logarithmic') {
                        score = Math.log10(prob * impact);
                      } else {
                        score = prob * impact;
                      }
                      const sortedThresholds = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
                      let cellColor = '#22c55e';
                      for (const threshold of sortedThresholds) {
                        if (score <= threshold.value) {
                          cellColor = threshold.color || '#22c55e';
                          break;
                        }
                      }
                      return (
                        <div
                          key={`preview-cell-${rowIdx}-${colIdx}`}
                          className="border border-gray-300"
                          style={{ backgroundColor: cellColor }}
                        />
                      );
                    })
                  ])}
                </div>
              </div>
            {/* Header */}
            <h3 className="text-lg font-semibold mb-3">Matrix Configuration</h3>
            <div className="flex items-center gap-4 mb-4">
              <span className="block text-sm font-medium">Matrix Calculation Type:</span>
              <button
                className={`px-3 py-1 rounded-l border text-xs ${matrixConfig.calculationType === 'linear' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
                onClick={() => {
                  const valueConfig = getDefaultThresholds(selectedMatrixDimension, 'linear');
                  const mergedThresholds = matrixConfig.scoreThresholds.map((t, i) => ({
                    ...t,
                    value: valueConfig.thresholds[i]?.value
                  }));
                  setMatrixConfig({
                    calculationType: 'linear',
                    probabilityLevels: [...matrixConfig.probabilityLevels],
                    impactLevels: [...matrixConfig.impactLevels],
                    scoreThresholds: mergedThresholds
                  });
                }}
              >
                Linear
              </button>
              <button
                className={`px-3 py-1 rounded-r border text-xs ${matrixConfig.calculationType === 'logarithmic' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
                onClick={() => {
                  const valueConfig = getDefaultThresholds(selectedMatrixDimension, 'logarithmic');
                  const mergedThresholds = matrixConfig.scoreThresholds.map((t, i) => ({
                    ...t,
                    value: valueConfig.thresholds[i]?.value
                  }));
                  setMatrixConfig({
                    calculationType: 'logarithmic',
                    probabilityLevels: [...matrixConfig.probabilityLevels],
                    impactLevels: [...matrixConfig.impactLevels],
                    scoreThresholds: mergedThresholds
                  });
                }}
              >
                Logarithmic
              </button>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <span className="block text-sm font-medium">Matrix Dimensions:</span>
              {[3, 4, 5, 6, 7].map(dim => (
                <button
                  key={dim}
                  className={`px-3 py-1 rounded border text-xs ${selectedMatrixDimension === dim ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
                  onClick={() => {
                    setSelectedMatrixDimension(dim);
                    const linearConfig = getDefaultThresholds(dim, 'linear');
                    const calcType = matrixConfig.calculationType || 'linear';
                    const valueConfig = getDefaultThresholds(dim, calcType);
                    const mergedThresholds = linearConfig.thresholds.map((t, i) => ({
                      ...t,
                      value: valueConfig.thresholds[i]?.value
                    }));
                    setMatrixConfig({
                      calculationType: calcType,
                      probabilityLevels: [...linearConfig.likelihoods],
                      impactLevels: [...linearConfig.impacts],
                      scoreThresholds: mergedThresholds
                    });
                  }}
                >
                  {dim}x{dim}
                </button>
              ))}
            </div>
            <div className="flex gap-4 mb-4">
              <button
                onClick={resetMatrixConfig}
                className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-xs"
              >
                Reset to Defaults
              </button>
              <button
                onClick={() => setShowScoreLabels(v => !v)}
                className={`px-3 py-1 rounded text-xs border ${showScoreLabels ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
              >
                {showScoreLabels ? 'Hide' : 'Show'} Score Labels
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Score Thresholds</label>
                <div className="space-y-2">
                  {matrixConfig.scoreThresholds.map((threshold, idx) => (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      <input
                        type="text"
                        value={threshold.label}
                        onChange={e => {
                          const newThresholds = [...matrixConfig.scoreThresholds];
                          newThresholds[idx].label = e.target.value;
                          setMatrixConfig({ ...matrixConfig, scoreThresholds: newThresholds });
                        }}
                        className="w-24 px-2 py-1 border rounded text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Likelihood Labels</label>
                <div className="space-y-1">
                  {matrixConfig.probabilityLevels.map((level, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={level}
                        onChange={(e) => {
                          const newLevels = [...matrixConfig.probabilityLevels];
                          newLevels[index] = e.target.value;
                          setMatrixConfig({...matrixConfig, probabilityLevels: newLevels});
                        }}
                        className="flex-1 px-2 py-1 border rounded text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Impact Labels</label>
                <div className="space-y-1">
                  {matrixConfig.impactLevels.map((level, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={level}
                        onChange={(e) => {
                          const newLevels = [...matrixConfig.impactLevels];
                          newLevels[index] = e.target.value;
                          setMatrixConfig({...matrixConfig, impactLevels: newLevels});
                        }}
                        className="flex-1 px-2 py-1 border rounded text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Management Dashboard - Moved to Top */}
      <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 text-center">Risk Management Dashboard</h3>
        
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {/* Action Progress Card */}
          <div
            className="bg-white w-full rounded-lg p-4 shadow-sm relative"
            onMouseEnter={e => setDashboardTooltip({ idx: 0, x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setDashboardTooltip({ idx: null, x: 0, y: 0 })}
          >
            <h4 className="font-medium text-sm text-gray-600 mb-2">Action Progress</h4>
            <div className="flex items-center">
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(((filteredRisks.filter(r => r.status === 'Completed').length / Math.max(filteredRisks.length, 1)) * 100))}
              </div>
              <div className="text-sm text-gray-500 ml-1">%</div>
            </div>
            <div className="text-xs text-gray-500 mt-1">Actions completed</div>
            {dashboardTooltip.idx === 0 && (
              <div style={{ position: 'fixed', left: dashboardTooltip.x + 10, top: dashboardTooltip.y + 10, zIndex: 1000 }} className="bg-white border rounded shadow-lg p-3 text-xs w-80">
                <div className="font-semibold mb-1">Completed Risk(s):</div>
                {filteredRisks.filter(r => r.status === 'Completed').length === 0 ? (
                  <div className="text-gray-400">None</div>
                ) : (
                  filteredRisks.filter(r => r.status === 'Completed').map(risk => (
                    <div key={risk.id} className="mb-1">
                      <span className="font-bold">ID:</span> {risk.id} <span className="font-bold">Score:</span> {getRiskPriority(risk.probability, risk.impact).level} <span className="font-bold">Status:</span> {risk.status}
                      <div className="ml-4 text-gray-700">{risk.description}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Overdue Actions Card */}
          <div
            className="bg-white w-full rounded-lg p-4 shadow-sm relative"
            onMouseEnter={e => setDashboardTooltip({ idx: 1, x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setDashboardTooltip({ idx: null, x: 0, y: 0 })}
          >
            <h4 className="font-medium text-sm text-gray-600 mb-2">Overdue Actions</h4>
            <div className="flex items-center">
              <div className="text-2xl font-bold text-red-600">
                {filteredRisks.filter(r => r.status === 'Overdue').length}
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">Require attention</div>
            {dashboardTooltip.idx === 1 && (
              <div style={{ position: 'fixed', left: dashboardTooltip.x + 10, top: dashboardTooltip.y + 10, zIndex: 1000 }} className="bg-white border rounded shadow-lg p-3 text-xs w-80">
                <div className="font-semibold mb-1">Overdue Risk(s):</div>
                {filteredRisks.filter(r => r.status === 'Overdue').length === 0 ? (
                  <div className="text-gray-400">None</div>
                ) : (
                  filteredRisks.filter(r => r.status === 'Overdue').map(risk => (
                    <div key={risk.id} className="mb-1">
                      <span className="font-bold">ID:</span> {risk.id} <span className="font-bold">Score:</span> {getRiskPriority(risk.probability, risk.impact).level} <span className="font-bold">Status:</span> {risk.status}
                      <div className="ml-4 text-gray-700">{risk.description}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Risk Exposure Card */}
          <div
            className="bg-white w-full rounded-lg p-4 shadow-sm relative"
            onMouseEnter={e => setDashboardTooltip({ idx: 2, x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setDashboardTooltip({ idx: null, x: 0, y: 0 })}
          >
            <h4 className="font-medium text-sm text-gray-600 mb-2">Risk Exposure</h4>
            <div className="flex items-center">
              <div className="text-2xl font-bold text-orange-600">
                {filteredRisks.filter(r => ['Critical', 'High'].includes(getRiskPriority(r.probability, r.impact).level)).length}
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">High+ priority risks</div>
            {dashboardTooltip.idx === 2 && (
              <div style={{ position: 'fixed', left: dashboardTooltip.x + 10, top: dashboardTooltip.y + 10, zIndex: 1000 }} className="bg-white border rounded shadow-lg p-3 text-xs w-80">
                <div className="font-semibold mb-1">High/Critical Risk(s):</div>
                {filteredRisks.filter(r => ['Critical', 'High'].includes(getRiskPriority(r.probability, r.impact).level)).length === 0 ? (
                  <div className="text-gray-400">None</div>
                ) : (
                  filteredRisks.filter(r => ['Critical', 'High'].includes(getRiskPriority(r.probability, r.impact).level)).map(risk => (
                    <div key={risk.id} className="mb-1">
                      <span className="font-bold">ID:</span> {risk.id} <span className="font-bold">Score:</span> {getRiskPriority(risk.probability, risk.impact).level} <span className="font-bold">Status:</span> {risk.status}
                      <div className="ml-4 text-gray-700">{risk.description}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${hideFilters ? '' : 'grid lg:grid-cols-4 gap-6'}`}>
        {!hideFilters && (
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg p-6 mb-6 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Grid size={20} className="text-blue-600" />
                Filters
              </h3>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 rounded-md hover:bg-blue-50 transition-colors"
              >
                Clear All
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({...filters, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                <select
                  value={filters.action}
                  onChange={(e) => setFilters({...filters, action: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="">All Actions</option>
                  {riskActions.map(action => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Owner</label>
                <select
                  value={filters.owner}
                  onChange={(e) => setFilters({...filters, owner: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="">All Owners</option>
                  {getUniqueOwners().map(owner => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
                <select
                  value={filters.deadline}
                  onChange={(e) => setFilters({...filters, deadline: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="">All Deadlines</option>
                  <option value="overdue">Overdue</option>
                  <option value="thisWeek">Due This Week</option>
                  <option value="noDeadline">No Deadline Set</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-sm text-gray-700 text-center">
                  Showing <span className="font-bold text-blue-600">{filteredRisks.length}</span> of <span className="font-bold text-gray-800">{risks.length}</span> risks
                </div>
              </div>
            </div>
          </div>

          {/* Only keep Load Sample Data button */}
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔥 Load Sample Data button clicked!');
                try {
                  loadSampleData();
                } catch (error) {
                  console.error('❌ Error loading sample data:', error);
                }
              }}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 active:bg-blue-800 flex items-center justify-center gap-2 font-medium transition-colors shadow-sm"
            >
              <RefreshCw size={18} />
              Load {(() => {
                const sizes = ['3', '8', '20'];
                const currentIndex = sizes.indexOf(sampleDataSet);
                const nextSize = sizes[(currentIndex + 1) % sizes.length];
                return `${nextSize} Sample Records`;
              })()} (Current: {risks.length})
            </button>
            <div className="mt-2 text-xs text-gray-500 text-center">
              Cycles through 3 → 8 → 20 records • Clean single-digit IDs
            </div>
          </div>
        </div>
        )}

        {(viewMode === 'kanban' || viewMode === 'both') && (
        <div className={`${hideFilters ? 'lg:col-span-4' : 'lg:col-span-3'}`}>
          <div className="mb-4">
            {!hideKanban && (
            <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-xl font-semibold flex-shrink-0">Current Risks ({filteredRisks.length})</h3>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-medium text-gray-700">Group by:</label>
                <select
                  value={kanbanGrouping}
                  onChange={(e) => setKanbanGrouping(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {['Status', 'Category', 'Action', 'Owner'].map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddRiskModal}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 font-medium shadow"
                >
                  <Plus size={18} />
                  Add Risk
                </button>
                <button
                  onClick={() => setKanbanFullscreen(true)}
                  className="p-2 rounded hover:bg-gray-200 text-gray-600"
                  title="Expand Kanban"
                >
                  <Maximize2 size={16} />
                </button>
              </div>
            </div>
            )}
          </div>
          <div className="bg-white border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Risk Impact Matrix</h2>
            <div className="relative bg-gray-50 rounded-lg" style={{ height: '60vh', width: '100%' }}>
              {/* Impact label at top */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-sm font-semibold text-gray-700">
                <span className="pointer-events-none select-none">Impact</span>
              </div>
              
              {/* Likelihood label on left (vertical) */}
              <div className="absolute -left-10 top-1/2 transform -translate-y-1/2 -rotate-90 origin-center text-sm font-semibold text-gray-700 z-10 pointer-events-none select-none">
                Likelihood
              </div>
              
              <div
                className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] grid"
                style={{
                  gridTemplateColumns: `repeat(${matrixConfig.impactLevels.length + 1}, 1fr)`,
                  gridTemplateRows: `repeat(${matrixConfig.probabilityLevels.length + 1}, 1fr)`
                }}
              >
                {/* Top-left empty cell */}
                <div className="border border-gray-300 flex items-center justify-center bg-white" />
                {/* Column headers */}
                {matrixConfig.impactLevels.map((label, idx) => (
                  <div
                    key={`col-header-${label}`}
                    className="border border-gray-300 flex items-center justify-center bg-white text-xs font-semibold text-gray-700"
                  >
                    {label}
                  </div>
                ))}
                {/* Row headers and matrix cells */}
                {matrixConfig.probabilityLevels.slice().reverse().map((rowLabel, rowIdx) => [
                  // Row header
                  <div
                    key={`row-header-${rowLabel}`}
                    className="border border-gray-300 flex items-center justify-center bg-white text-xs font-semibold text-gray-700"
                  >
                    {rowLabel}
                  </div>,
                  // Matrix cells for this row
                  ...matrixConfig.impactLevels.map((_, colIdx) => {
                    const prob = matrixConfig.probabilityLevels.length - rowIdx;
                    const impact = colIdx + 1;
                    let score;
                    if (matrixConfig.calculationType === 'logarithmic') {
                      score = Math.log10(prob * impact);
                    } else {
                      score = prob * impact;
                    }
                    // Find the matching threshold (sorted ascending)
                    const sortedThresholds = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
                    let cellColor = '#22c55e'; // default green
                    let scoreLabel = '';
                    for (const threshold of sortedThresholds) {
                      if (score <= threshold.value) {
                        cellColor = threshold.color || '#22c55e';
                        scoreLabel = threshold.label;
                        break;
                      }
                    }
                    return (
                      <div
                        key={`cell-${rowIdx}-${colIdx}`}
                        className="border border-gray-300 flex items-center justify-center"
                        style={{ backgroundColor: cellColor }}
                      >
                        {showScoreLabels && (
                          <span 
                            className="text-sm select-none"
                            style={{ 
                              color: '#000',
                              fontSize: '12px'
                            }}
                          >
                            {scoreLabel}
                          </span>
                        )}
                      </div>
                    );
                  })
                ])}
              </div>
              {/* Overlay risk dots and tooltips as before, but adjust their positioning to fit the new grid if needed */}
              <div className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] pointer-events-none">
                {(() => {
                  // Group risks by cell to apply honeycomb offsets
                  const cellMap = {};
                  filteredRisks.forEach(risk => {
                    const likelihoodValue = (risk.likelihood ?? risk.probability ?? 1); // fallback to probability for legacy data
                    const rowIdx = matrixConfig.probabilityLevels.length - likelihoodValue; // reverse order
                    const colIdx = risk.impact - 1;
                    const key = `${rowIdx}-${colIdx}`;
                    if (!cellMap[key]) cellMap[key] = [];
                    cellMap[key].push(risk);
                  });

                  const cellWidth = 100 / (matrixConfig.impactLevels.length + 1);
                  const cellHeight = 100 / (matrixConfig.probabilityLevels.length + 1);

                  // Predefined honeycomb offsets (dx,dy) relative to cellWidth/Height (fractions)
                  const offsets = [
                    [0, 0],
                    [-0.25, -0.15],
                    [0.25, -0.15],
                    [0.35, 0.15],
                    [0, 0.3],
                    [-0.35, 0.15],
                    [-0.25, -0.4],
                    [0.25, -0.4],
                  ];

                  const rendered = [];
                  Object.entries(cellMap).forEach(([key, risksInCell]) => {
                    const [rowIdx, colIdx] = key.split('-').map(Number);
                    const baseLeft = ((colIdx + 1) * cellWidth) + cellWidth / 2;
                    const baseTop = ((rowIdx + 1) * cellHeight) + cellHeight / 2;

                    risksInCell.forEach((risk, idx) => {
                      const offset = offsets[idx % offsets.length] || [0, 0];
                      const left = baseLeft + offset[0] * (cellWidth);
                      const top = baseTop + offset[1] * (cellHeight);

                      rendered.push(
                        <div
                          key={risk.id}
                          title={risk.description}
                          className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer z-10"
                          style={{ left: `${left}%`, top: `${top}%` }}
                          onClick={() => openRiskDetail(risk)}
                          onMouseEnter={(e) => setMatrixTooltip({ show: true, risk, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setMatrixTooltip({ show: false, risk: null, x: 0, y: 0 })}
                          onMouseMove={(e) => setMatrixTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }))}
                        >
                          <div className="relative">
                            <div
                              className="w-8 h-8 rounded-full border-2 border-white shadow-md flex items-center justify-center text-xs font-bold text-white"
                              style={{ backgroundColor: getColorForRisk(risk) }}
                            >
                              {risk.id}
                            </div>
                            {risk.status === 'Completed' && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-white absolute -bottom-1 -right-1 bg-green-600 rounded-full p-[1px]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    });
                  });

                  return rendered;
                })()}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Color By Selector */}
      <div className="bg-white border rounded-lg p-4 mt-6 w-full">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Circle Colors</h4>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Color by:</label>
            <select
              value={colorBy}
              onChange={(e) => setColorBy(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Category">Category</option>
              <option value="Status">Status</option>
              <option value="Action">Action</option>
              <option value="Owner">Action Owner</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          {legendItems.map(item => (
            <div key={item} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorMap[item] }}></div>
              <span className="text-sm text-gray-600">{item || 'Unassigned'}</span>
            </div>
          ))}
        </div>
      </div>

      {(!hideRegister) && (viewMode === 'table' || viewMode === 'both') && (
      <> 
      {/* Risk Register Table - now full width */}
      <div className="bg-white border rounded-lg p-6 mt-6 w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Risk Register</h2>
          <div className="flex gap-2">
            <button
              onClick={handleAddRiskModal}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm flex items-center gap-2"
            >
              <Plus size={16} />
              Add Risk
            </button>
            <button
              onClick={exportToSpreadsheet}
              className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
            >
              Export as Spreadsheet
            </button>
            <button
              onClick={exportToPDF}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
            >
              Export as PDF
            </button>
                          <button
              onClick={() => setRegisterFullscreen(true)}
              className="p-2 rounded hover:bg-gray-200 text-gray-600"
              title="Expand Risk Register"
            >
              <Maximize2 size={16} />
                          </button>
                        </div>
        </div>
        <div className="overflow-x-auto">
          <RiskRegisterTable />
      </div>
      </div>
      </>) }

      {/* Risk Detail Modal (fallback when no global context) */}
      {(!ctx || !ctx.openRiskDetail) && showRiskDetail && editingRisk && (() => {
        const maxDim = Math.max(matrixConfig.impactLevels.length, matrixConfig.probabilityLevels.length);
        const cellSize = maxDim >= 7 ? 50 : maxDim === 6 ? 60 : 80;
        const priority = getRiskPriority(
          editingRisk.probability ?? editingRisk.likelihood ?? 1,
          editingRisk.impact ?? 1
        );

        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Risk Details</h2>
                <button
                  onClick={closeRiskDetail}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Risk Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Risk Description
                  </label>
                  <textarea
                    value={editingRisk.description}
                    onChange={(e) => handleEditRiskChange('description', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Describe the risk..."
                  />
                </div>

                {/* Risk Assessment */}
                <div className="mb-4">
                  <div className="flex justify-center items-center p-6 bg-gray-50 rounded-lg border">
                    {/* Likelihood vertical label */}
                    <div className="flex items-center pr-4">
                      <span
                        className="text-base font-semibold text-gray-700"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                      >
                        Likelihood
                      </span>
                    </div>
                    <div className="inline-block">
                       {/* Axis label - Impact (top) */}
                       <div className="flex justify-center mb-4">
                         <span className="text-base font-semibold text-gray-700">Impact</span>
                       </div>
                      {/* Create a CSS Grid layout for perfect alignment */}
                       {/* Adapt matrix cell dimensions based on the overall matrix size so that even a 7×7 grid fits comfortably in the modal. */}
                      <div 
                         className="relative grid gap-0"
                        style={{
                           gridTemplateColumns: `120px repeat(${matrixConfig.impactLevels.length}, ${cellSize}px)`,
                           gridTemplateRows: `100px repeat(${matrixConfig.probabilityLevels.length}, ${cellSize}px)`
                        }}
                      >
                        {/* Top-left corner (empty) */}
                        <div></div>
                        
                        {/* Impact header and radio buttons (top row) */}
                        {matrixConfig.impactLevels.map((label, idx) => (
                           <div key={`impact-${idx}`} className="flex flex-col items-center justify-end gap-1 pb-2">
                             <span className="text-xs text-gray-700 text-center font-semibold leading-tight" 
                                   style={{ fontSize: '14px', maxWidth: 70, lineHeight: '1.1' }}
                                  title={label}>
                               {label.length > 6 ? label.substring(0, 5) + '.' : label}
                            </span>
                            <input
                              type="radio"
                              name="impact-selector"
                              checked={editingRisk.impact === idx + 1}
                              onChange={() => handleEditRiskChange('impact', idx + 1)}
                              className="accent-blue-600 scale-110"
                            />
                          </div>
                        ))}
                        
                        {/* Matrix rows with likelihood controls */}
                        {matrixConfig.probabilityLevels.slice().reverse().map((rowLabel, rowIdx) => [
                           // Row header with likelihood radio
                          <div key={`likelihood-${rowIdx}`} className="flex items-center justify-end gap-2 pr-2">
                             <span className="text-xs text-gray-700 text-right font-semibold" 
                                   style={{ fontSize: '14px', lineHeight: '1.1', maxWidth: 90 }}
                                  title={rowLabel}>
                               {rowLabel.length > 8 ? rowLabel.substring(0, 7) + '.' : rowLabel}
                            </span>
                            <input
                              type="radio"
                              name="likelihood-slider"
                              checked={editingRisk.probability === matrixConfig.probabilityLevels.length - rowIdx}
                              onChange={() => handleEditRiskChange('probability', matrixConfig.probabilityLevels.length - rowIdx)}
                              className="accent-blue-600 scale-110"
                            />
                          </div>,
                          
                          // Matrix cells for this row
                          ...matrixConfig.impactLevels.map((_, colIdx) => {
                            const prob = matrixConfig.probabilityLevels.length - rowIdx;
                            const impact = colIdx + 1;
                            let score;
                            if (matrixConfig.calculationType === 'logarithmic') {
                              score = Math.log10(prob * impact);
                            } else {
                              score = prob * impact;
                            }
                            // Find the matching threshold (sorted ascending)
                            const sortedThresholds = [...matrixConfig.scoreThresholds].sort((a, b) => a.value - b.value);
                            let cellColor = '#22c55e';
                            let scoreLabel = '';
                            for (const threshold of sortedThresholds) {
                              if (score <= threshold.value) {
                                cellColor = threshold.color || '#22c55e';
                                scoreLabel = threshold.label;
                                break;
                              }
                            }
                            const isSelected = editingRisk.probability === prob && editingRisk.impact === impact;
                            
                            return (
                              <div
                                key={`cell-${rowIdx}-${colIdx}`}
                                className="border border-gray-300 flex items-center justify-center relative cursor-pointer hover:opacity-90 transition-opacity"
                                 style={{ backgroundColor: cellColor, width: cellSize, height: cellSize }}
                                onClick={() => {
                                  handleEditRiskChange('probability', prob);
                                  handleEditRiskChange('impact', impact);
                                }}
                              >
                                {showScoreLabels && (
                                  <span 
                                    className="text-xs select-none" 
                                    style={{ 
                                      color: '#000',
                                      fontSize: '10px',
                                      pointerEvents: 'none'
                                    }}
                                  >
                                    {scoreLabel}
                                  </span>
                                )}
                                {isSelected && (
                                  <div 
                                    className="absolute rounded-full bg-blue-600 border-2 border-white shadow-lg z-10" 
                                    style={{ 
                                       width: 24, 
                                       height: 24,
                                      left: '50%', 
                                      top: '50%', 
                                      transform: 'translate(-50%, -50%)',
                                      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.4)'
                                    }} 
                                  />
                                )}
                              </div>
                            );
                          })
                        ])}
                      </div>
                      
                       {/* Likelihood axis label */}
                       {/* Removed absolute likelihood label since flex label added */}
                    </div>
                  </div>
                </div>

                {/* Priority Display */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Risk Score:</span>
                    <span
                      className="px-3 py-1 rounded text-sm font-medium"
                      style={{ backgroundColor: priority.color, color: '#fff' }}
                    >
                      {priority.level}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Score: {(() => {
                      let score;
                      if (matrixConfig.calculationType === 'logarithmic') {
                        score = Math.log10(editingRisk.probability * editingRisk.impact);
                      } else {
                        score = editingRisk.probability * editingRisk.impact;
                      }
                      return score.toFixed(2);
                    })()}
                  </div>
                </div>

                {/* Category and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={editingRisk.category}
                      onChange={(e) => handleEditRiskChange('category', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={editingRisk.status}
                      onChange={(e) => handleEditRiskChange('status', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {statusOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Action Plan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Strategy
                  </label>
                  <select
                    value={editingRisk.action}
                    onChange={(e) => handleEditRiskChange('action', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {riskActions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Description
                  </label>
                  <textarea
                    value={editingRisk.actionDescription}
                    onChange={(e) => handleEditRiskChange('actionDescription', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Describe the action plan..."
                  />
                </div>

                {/* Owner and Deadline */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action Owner
                    </label>
                    <input
                      type="text"
                      value={editingRisk.actionOwner}
                      onChange={(e) => handleEditRiskChange('actionOwner', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Who is responsible?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Date
                    </label>
                    <input
                      type="date"
                      value={editingRisk.deadline}
                      onChange={(e) => handleEditRiskChange('deadline', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                <button
                  onClick={closeRiskDetail}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveRiskChanges}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {kanbanFullscreen && (
        <div className="fixed inset-0 bg-white z-50 p-4 overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold">Kanban Board</h2>
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Group by:</label>
                <select
                  value={kanbanGrouping}
                  onChange={(e) => setKanbanGrouping(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {['Status', 'Category', 'Action', 'Owner'].map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={() => setKanbanFullscreen(false)}
              className="p-2 rounded hover:bg-gray-200 text-gray-600"
              title="Close Fullscreen"
            >
              <Minimize2 size={20} />
            </button>
          </div>
          {/* Fullscreen Kanban grid with drag-and-drop */}
          <div
            className="grid gap-4 h-[calc(100vh-120px)] overflow-auto"
            style={{ gridTemplateColumns: `repeat(${getKanbanColumns().length}, minmax(0, 1fr))` }}
          >
            {getKanbanColumns().map((columnValue) => {
              const field = kanbanFieldMap[kanbanGrouping];
              const columnRisks = filteredRisks.filter(risk => {
                if (field === 'actionOwner') {
                  return (risk.actionOwner || 'Unassigned') === columnValue;
                }
                return risk[field] === columnValue;
              });
              const isDropTarget = dragOverColumn === columnValue;
              
              return (
                <div 
                  key={columnValue} 
                  className={`bg-gray-50 rounded-lg p-4 transition-all duration-200 ${
                    isDropTarget ? 'bg-blue-100 border-2 border-blue-300 border-dashed' : ''
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, columnValue)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, columnValue)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-lg text-gray-700 truncate" title={columnValue}>
                      {columnValue || 'Unassigned'}
                    </h4>
                    <span className="bg-gray-200 text-gray-700 text-sm px-2 py-1 rounded-full">
                      {columnRisks.length}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {columnRisks.map(risk => {
                      const priority = getRiskPriority(risk.probability, risk.impact);
                      const isDragging = draggedRisk && draggedRisk.id === risk.id;
                      
                      return (
                        <div 
                          key={risk.id} 
                          title={risk.description}
                          className={`bg-white border rounded-lg p-3 hover:shadow-sm transition-all cursor-move ${
                            isDragging ? 'opacity-50 scale-95' : 'hover:scale-102'
                          }`}
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, risk)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => {
                            if (!isDragging && !e.target.closest('button')) {
                              openRiskDetail(risk);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-1 mb-1">
                                <span className={`w-2 h-2 rounded-full ${categoryColors[risk.category]}`}></span>
                                <span className="text-xs text-gray-500 uppercase">{risk.category}</span>
                              </div>
                              <p className="text-sm text-gray-800 font-medium mb-2 line-clamp-3">{risk.description}</p>
                              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                <span>L:{risk.probability}</span>
                                <span>I:{risk.impact}</span>
                                <span>Owner: {risk.actionOwner || 'Unassigned'}</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRisk(risk.id);
                              }}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span 
                              className="px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: priority.color }}
                            >
                              {priority.level}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs border ${actionColors[risk.action]}`}>
                              {risk.action}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {columnRisks.length === 0 && (
                      <div className={`text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg ${
                        isDropTarget ? 'border-blue-300 bg-blue-50' : ''
                      }`}>
                        {isDropTarget ? 'Drop risk here' : 'No risks'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Matrix tooltip */}
      {matrixTooltip.show && matrixTooltip.risk && (
        <div
          className="fixed bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm z-50 max-w-xs"
          style={{ left: matrixTooltip.x + 10, top: matrixTooltip.y + 10, pointerEvents: 'none' }}
        >
          <div className="font-semibold text-gray-800 mb-2">{matrixTooltip.risk.description}</div>
          <div className="space-y-1 text-xs text-gray-600">
            <div><span className="font-medium">ID:</span> {matrixTooltip.risk.id}</div>
            <div><span className="font-medium">Category:</span> {matrixTooltip.risk.category}</div>
            <div><span className="font-medium">Likelihood:</span> {matrixTooltip.risk.probability}</div>
            <div><span className="font-medium">Impact:</span> {matrixTooltip.risk.impact}</div>
            <div><span className="font-medium">Action:</span> {matrixTooltip.risk.action}</div>
            <div><span className="font-medium">Owner:</span> {matrixTooltip.risk.actionOwner || 'Unassigned'}</div>
            <div><span className="font-medium">Deadline:</span> {matrixTooltip.risk.deadline || 'Not set'}</div>
            <div><span className="font-medium">Status:</span> {matrixTooltip.risk.status}</div>
          </div>
        </div>
      )}

      {/* Fullscreen Risk Register */}
      {registerFullscreen && (
        <div className="fixed inset-0 bg-white z-[70] p-6 overflow-auto flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Risk Register (Full Screen)</h2>
            <button
              onClick={() => setRegisterFullscreen(false)}
              className="p-2 rounded hover:bg-gray-200 text-gray-600"
              title="Close"
            >
              <Minimize2 size={18} />
            </button>
          </div>
          <div className="overflow-x-auto flex-1">
            <RiskRegisterTable />
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskImpactMatrix;